import { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen, faSpinner, faArrowUpRightFromSquare, faChevronLeft, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import './ProductsPreviewModal.css';

// The paginated product list (10/page) of one catalog. Extracted from
// ProductsPreviewModal so the SAME view renders both in that modal AND inline in
// the Products page source cards (e.g. the Wix card). Read-only.
const prettify = (k) =>
  String(k || '').replace(/_(gb|tb|mb|mah|kg|g|cm|mm|inch|in|km|l|ml|ohm|bar|w|hz|mp|px)$/i, '')
    .replace(/_/g, ' ').trim();

const buildSpecs = (attributes) => {
  if (!attributes || typeof attributes !== 'object') return '';
  return Object.entries(attributes)
    .filter(([k, v]) => k !== 'title' && v !== null && v !== '' && v !== undefined)
    .slice(0, 5)
    .map(([k, v]) => `${prettify(k)}: ${v}`)
    .join(' · ');
};

const fmtPrice = (price, currency) => {
  if (price === null || price === undefined || price === '') return null;
  const n = typeof price === 'number' ? price : parseFloat(price);
  if (Number.isNaN(n)) return null;
  const hasCents = Math.abs(n % 1) > 0.001;
  const s = n.toLocaleString(undefined, { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 });
  return currency ? `${s} ${currency}` : s;
};

// onTotal(total) lets a parent show the count in its own header. `search` filters
// by product title across the whole catalog (server-side, so it spans all pages).
export default function ProductsPreviewList({ botId, knowledgeId, search = '', onTotal }) {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // A new catalog OR a new search term resets to the first page.
  useEffect(() => { setPage(1); }, [knowledgeId, search]);

  const fetchPage = useCallback(async () => {
    if (!knowledgeId || !botId) return;
    setLoading(true);
    setError(null);
    try {
      const q = search && search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}products?bot_id=${encodeURIComponent(botId)}&knowledge_id=${knowledgeId}&page=${page}${q}`
      );
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      setProducts(data.products || []);
      setPages(data.pages || 1);
      if (onTotal) onTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading products preview:', err);
      setError(t('pa_file_error'));
    } finally {
      setLoading(false);
    }
  }, [knowledgeId, botId, page, search, t, onTotal]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#5616ea" />
      </Box>
    );
  }
  if (error) {
    return <p style={{ color: '#d23f3f', padding: '8px 2px' }}>{error}</p>;
  }
  if (!products.length) {
    return (
      <p style={{ color: '#8a8a96', padding: '14px 2px', fontSize: 13.5 }}>
        {search ? t('pp_no_results', 'No products match your search.') : t('products_empty', 'No products yet.')}
      </p>
    );
  }

  return (
    <>
      <div className="pp-list">
        {products.map((p, i) => {
          const price = fmtPrice(p.price, p.currency);
          const specs = buildSpecs(p.attributes);
          return (
            <div className="pp-row" key={i}>
              <div className="pp-thumb">
                {p.image_url
                  ? <img src={p.image_url} alt="" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <FontAwesomeIcon icon={faBoxOpen} />}
              </div>
              <div className="pp-main">
                <div className="pp-title-row">
                  <span className="pp-title">{p.title || t('product_untitled')}</span>
                  {price && <span className="pp-price">{price}</span>}
                </div>
                {specs && <span className="pp-specs">{specs}</span>}
              </div>
              {p.product_url && (
                <a className="pp-link" href={p.product_url} target="_blank" rel="noopener noreferrer" title={t('product_view')}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              )}
            </div>
          );
        })}
      </div>

      {pages > 1 && (
        <div className="pp-pager">
          <button className="pp-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="pp-page-info">{t('pp_page')} {page} / {pages}</span>
          <button className="pp-page-btn" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </>
  );
}
